import React from 'react';
import { TextField, Dropdown, IDropdownOption, DefaultButton, PrimaryButton, IconButton, Label } from '@fluentui/react';
import { IStorefrontSettings } from '../../../models/Storefront';
import { ILintResult } from './searchPageConfigLint';
import RawJsonConfigEditor from './RawJsonConfigEditor';
import {
    LOCALIZATION_ENTRIES,
    LOCALIZATION_GROUPS,
    parseLocalizationConfig,
    serializeLocalizationConfig,
} from './localizationConfig';

// Validate raw localizationConfig JSON: a flat object of string -> string. Unknown keys are allowed
// (warned), non-string values are errors — so a merchant can paste a set from another storefront.
const lintLocalization = (raw: string): ILintResult => {
    const errors: string[] = [];
    const warnings: string[] = [];
    if (raw.trim() === '') return { valid: true, errors, warnings };
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        return { valid: false, errors: [`Invalid JSON: ${(e as Error).message}`], warnings };
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return { valid: false, errors: ['Must be a JSON object of key -> label.'], warnings };
    }
    const known = new Set(LOCALIZATION_ENTRIES.map((e) => e.key));
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof v !== 'string') errors.push(`"${k}" must be a string.`);
        if (!known.has(k)) warnings.push(`"${k}" is not a known localization key.`);
    }
    return { valid: errors.length === 0, errors, warnings };
};

interface IStorefrontLocalizationEditorProps {
    storefrontSettings: IStorefrontSettings;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
}

const CUSTOM_CONTEXT = '__custom__';

const entryByKey = LOCALIZATION_ENTRIES.reduce((acc, e) => {
    acc[e.key] = e;
    return acc;
}, {} as Record<string, typeof LOCALIZATION_ENTRIES[number]>);

// Per-storefront localization editor. Shows only the overrides the merchant has actually set as a
// grid (context / field / value / remove), with an Add flow: pick a context, then a field within
// it, then a value. A "Custom" context lets you enter an arbitrary key (in case the shopper app is
// ahead of / behind this manifest). Values are editable inline; rows can be removed.
const StorefrontLocalizationEditor: React.FC<IStorefrontLocalizationEditorProps> = (props) => {
    const overrides = React.useMemo(
        () => parseLocalizationConfig(props.storefrontSettings.localizationConfig),
        [props.storefrontSettings.localizationConfig]
    );

    const commit = (next: Record<string, string>) => {
        props.onStorefrontSettingsChange({
            ...props.storefrontSettings,
            localizationConfig: serializeLocalizationConfig(next),
        });
    };

    const setOverride = (key: string, value: string) => commit({ ...overrides, [key]: value });
    const removeOverride = (key: string) => {
        const next = { ...overrides };
        delete next[key];
        commit(next);
    };

    // --- Add flow state ---
    const [adding, setAdding] = React.useState(false);
    const [context, setContext] = React.useState<string | undefined>();
    const [fieldKey, setFieldKey] = React.useState<string | undefined>();
    const [customKey, setCustomKey] = React.useState('');
    const [value, setValue] = React.useState('');

    const resetForm = () => {
        setAdding(false);
        setContext(undefined);
        setFieldKey(undefined);
        setCustomKey('');
        setValue('');
    };

    const contextOptions: IDropdownOption[] = [
        ...LOCALIZATION_GROUPS.map((g) => ({ key: g, text: g })),
        { key: CUSTOM_CONTEXT, text: 'Custom (arbitrary key)' },
    ];

    // Fields available in the chosen context, excluding keys already overridden.
    const fieldOptions: IDropdownOption[] = context && context !== CUSTOM_CONTEXT
        ? LOCALIZATION_ENTRIES
            .filter((e) => e.group === context && overrides[e.key] === undefined)
            .map((e) => ({ key: e.key, text: e.label, data: e.default }))
        : [];

    const isCustom = context === CUSTOM_CONTEXT;
    const resolvedKey = isCustom ? customKey.trim() : fieldKey;
    const canSave = !!resolvedKey && value.trim() !== '' && !(isCustom && overrides[customKey.trim()] !== undefined);

    const onPickContext = (_: unknown, opt?: IDropdownOption) => {
        setContext(opt?.key as string);
        setFieldKey(undefined);
        setCustomKey('');
        setValue('');
    };

    const onPickField = (_: unknown, opt?: IDropdownOption) => {
        setFieldKey(opt?.key as string);
        // Prefill the value with the field's English default so the merchant edits from a baseline.
        if (opt?.data && value.trim() === '') setValue(String(opt.data));
    };

    const saveNew = () => {
        if (!canSave || !resolvedKey) return;
        setOverride(resolvedKey, value.trim());
        resetForm();
    };

    const rows = Object.keys(overrides);

    const cellHead: React.CSSProperties = { textAlign: 'left', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#605e5c', padding: '6px 12px', borderBottom: '1px solid #edebe9', fontWeight: 600 };
    const cell: React.CSSProperties = { padding: '6px 12px', borderBottom: '1px solid #f3f2f1', verticalAlign: 'middle' };

    return (
        <div style={{ padding: '0.5em 0', maxWidth: 900 }}>
            <p style={{ color: '#605e5c', fontSize: 13, margin: '0 0 1em 0' }}>
                Override individual storefront labels — e.g. to translate the storefront or re-brand terminology.
                Only the overrides you add are stored; everything else uses its built-in default.
            </p>

            {rows.length === 0 && !adding && (
                <div style={{ padding: '1.5em', border: '1px dashed #d1d1d1', borderRadius: 6, textAlign: 'center', color: '#605e5c', marginBottom: '1em' }}>
                    No localization overrides yet.
                </div>
            )}

            {rows.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1em' }}>
                    <thead>
                        <tr>
                            <th style={{ ...cellHead, width: '18%' }}>Context</th>
                            <th style={{ ...cellHead, width: '30%' }}>Field</th>
                            <th style={cellHead}>Value</th>
                            <th style={{ ...cellHead, width: 44 }} aria-label='Remove' />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((key) => {
                            const entry = entryByKey[key];
                            return (
                                // Stamped from the override's own key rather than a registered id:
                                // these come from data, so nothing can enumerate them ahead of time,
                                // but a link to one a storefront actually sets will find its row.
                                <tr key={key} data-setting-id={`storefront.localization.${key}`}>
                                    <td style={cell}>{entry ? entry.group : 'Custom'}</td>
                                    <td style={cell}>
                                        <div>{entry ? entry.label : key}</div>
                                        <code style={{ fontSize: 11, color: '#a19f9d' }}>{key}</code>
                                    </td>
                                    <td style={cell}>
                                        <TextField
                                            value={overrides[key]}
                                            placeholder={entry?.default}
                                            onChange={(_, v) => setOverride(key, v ?? '')}
                                            underlined
                                        />
                                    </td>
                                    <td style={{ ...cell, textAlign: 'center' }}>
                                        <IconButton
                                            iconProps={{ iconName: 'Delete' }}
                                            title='Remove override'
                                            onClick={() => removeOverride(key)}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {adding ? (
                <div style={{ padding: '1em', border: '1px solid #edebe9', borderRadius: 6, background: '#faf9f8' }}>
                    <Label styles={{ root: { marginBottom: 8 } }}>Add an override</Label>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <Dropdown
                            label='Context'
                            placeholder='Select a context'
                            options={contextOptions}
                            selectedKey={context ?? null}
                            onChange={onPickContext}
                            styles={{ root: { minWidth: 180 } }}
                        />
                        {isCustom ? (
                            <TextField
                                label='Custom key'
                                placeholder='localization_area_meaning'
                                value={customKey}
                                onChange={(_, v) => setCustomKey(v ?? '')}
                                styles={{ root: { minWidth: 240 } }}
                                errorMessage={customKey.trim() !== '' && overrides[customKey.trim()] !== undefined ? 'That key already has an override.' : undefined}
                            />
                        ) : (
                            <Dropdown
                                label='Field'
                                placeholder={context ? 'Select a field' : 'Pick a context first'}
                                options={fieldOptions}
                                selectedKey={fieldKey ?? null}
                                onChange={onPickField}
                                disabled={!context}
                                styles={{ root: { minWidth: 240 } }}
                            />
                        )}
                        <TextField
                            label='Value'
                            placeholder={fieldKey ? entryByKey[fieldKey]?.default : 'Replacement label'}
                            value={value}
                            onChange={(_, v) => setValue(v ?? '')}
                            styles={{ root: { flex: '1 1 200px', minWidth: 200 } }}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <PrimaryButton text='Add' onClick={saveNew} disabled={!canSave} />
                        <DefaultButton text='Cancel' onClick={resetForm} />
                    </div>
                </div>
            ) : (
                <DefaultButton iconProps={{ iconName: 'Add' }} text='Add Override' onClick={() => setAdding(true)} />
            )}

            <div style={{ marginTop: '2em', paddingTop: '1em', borderTop: '1px solid #edebe9' }}>
                <RawJsonConfigEditor
                    label='Advanced: raw JSON (copy between storefronts)'
                    value={props.storefrontSettings.localizationConfig}
                    lint={lintLocalization}
                    onChange={(json) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, localizationConfig: json })}
                    keysHelp={'A flat object of localization key -> replacement label. Paste a set here to port it from another storefront.'}
                />
            </div>
        </div>
    );
};

export default StorefrontLocalizationEditor;
